// Procedural enemy art (Canvas2D, built once in BootScene). One spritesheet per
// enemy type ('enemy_<id>') with idle/move/windup/attack/stagger(/leap) frames,
// debris frames ('gib0'..), the rotating arm+weapon frame ('gun') where the rig
// has one, plus an elite outline mask for every frame ('<frame>_o', white,
// tinted at runtime). The bulwark's shield is a separate texture.
//
// Style (art bible): hostile silhouettes, dark steel with threat red/orange
// accents, glowing sensors, 2 px ink outline, upper-left key light. Poses come
// from rig.ts so the runtime attachment points match the drawn frames.

import type Phaser from 'phaser';
import { COLORS, css, mix, shade } from '../../config/palette';
import { ENEMY_IDS, type EnemyId } from '../../core/types';
import {
  ENEMY_ANIMS,
  ENEMY_FX,
  RIGS,
  bulwarkPose,
  enemyAnimKey,
  frameName,
  hornetPose,
  lancerPose,
  outlineFrame,
  trooperPose,
  type BipedPose,
  type HornetPose,
  type LegPose,
  type RigDef,
} from './rig';

type G = CanvasRenderingContext2D;

interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

const TAU = Math.PI * 2;

/** Enemy material palette (derived from the global art-bible palette). */
const MAT = {
  ink: COLORS.ink,
  armor: 0x3b4657,
  armorDark: 0x29313d,
  plate: 0x4d5a6d,
  frame: 0x1d232d,
  joint: 0x12161d,
  metal: 0x6c7b90,
  paint: 0xc42d48,
  hazard: COLORS.threatHot,
  sensor: COLORS.threat,
  hot: COLORS.threatHot,
  energy: 0xff6a3d,
} as const;

// ───────────────────────────── Painter primitives ─────────────────────────────

function boxOf(pts: readonly number[]): Box {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (let i = 0; i < pts.length; i += 2) {
    x0 = Math.min(x0, pts[i]);
    x1 = Math.max(x1, pts[i]);
    y0 = Math.min(y0, pts[i + 1]);
    y1 = Math.max(y1, pts[i + 1]);
  }
  return { x0, y0, x1, y1 };
}

function polyPath(pts: readonly number[]): Path2D {
  const p = new Path2D();
  p.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) p.lineTo(pts[i], pts[i + 1]);
  p.closePath();
  return p;
}

function rrectPath(x: number, y: number, w: number, h: number, r: number): Path2D {
  const p = new Path2D();
  const rr = Math.min(r, w / 2, h / 2);
  p.moveTo(x + rr, y);
  p.arcTo(x + w, y, x + w, y + h, rr);
  p.arcTo(x + w, y + h, x, y + h, rr);
  p.arcTo(x, y + h, x, y, rr);
  p.arcTo(x, y, x + w, y, rr);
  p.closePath();
  return p;
}

function ellipsePath(cx: number, cy: number, rx: number, ry: number, rot = 0): Path2D {
  const p = new Path2D();
  p.ellipse(cx, cy, rx, ry, rot, 0, TAU);
  return p;
}

/** Tapered limb segment with round ends. */
function capsule(ax: number, ay: number, bx: number, by: number, ra: number, rb: number): { path: Path2D; box: Box } {
  const a = Math.atan2(by - ay, bx - ax);
  const p = new Path2D();
  p.arc(bx, by, rb, a - Math.PI / 2, a + Math.PI / 2);
  p.arc(ax, ay, ra, a + Math.PI / 2, a + Math.PI * 1.5);
  p.closePath();
  const r = Math.max(ra, rb);
  return { path: p, box: { x0: Math.min(ax, bx) - r, y0: Math.min(ay, by) - r, x1: Math.max(ax, bx) + r, y1: Math.max(ay, by) + r } };
}

interface ShadeOpts {
  /** Highlight multiplier at the lit (upper-left) corner. */
  hi?: number;
  /** Shadow multiplier at the far corner. */
  lo?: number;
  /** 0..1 how strongly the rim light tends to the edge-highlight colour. */
  rim?: number;
  /** Separation line width. */
  line?: number;
}

/** Solid part lit from the upper left: diagonal gradient, rim light, core shadow, ink separation line. */
function plate(g: G, path: Path2D, b: Box, base: number, o: ShadeOpts = {}): void {
  const grad = g.createLinearGradient(b.x0, b.y0, b.x1, b.y1);
  grad.addColorStop(0, css(shade(base, o.hi ?? 1.5)));
  grad.addColorStop(0.48, css(base));
  grad.addColorStop(1, css(shade(base, o.lo ?? 0.55)));
  g.fillStyle = grad;
  g.fill(path);
  finish(g, path, base, o);
}

/** Translucent key-light pass for parts whose base is not a flat colour (striped, painted). */
function lightOverlay(g: G, path: Path2D, b: Box): void {
  const grad = g.createLinearGradient(b.x0, b.y0, b.x1, b.y1);
  grad.addColorStop(0, 'rgba(255,255,255,0.2)');
  grad.addColorStop(0.5, 'rgba(0,0,0,0)');
  grad.addColorStop(1, 'rgba(0,0,0,0.42)');
  g.fillStyle = grad;
  g.fill(path);
}

/** A plate with hazard stripes across it, lit consistently. */
function striped(g: G, path: Path2D, b: Box, base: number, stripe: number, width: number, gap: number, angle: number, o: ShadeOpts = {}): void {
  g.fillStyle = css(base);
  g.fill(path);
  stripes(g, path, b, stripe, width, gap, angle);
  lightOverlay(g, path, b);
  finish(g, path, base, o);
}

/** Rim light, core shadow and separation line shared by every plate. */
function finish(g: G, path: Path2D, base: number, o: ShadeOpts): void {
  // Rim light: the path shifted down-right, stroked and clipped inside, leaves a lit sliver on the upper-left edges.
  g.save();
  g.clip(path);
  g.translate(1.3, 1.3);
  g.lineWidth = 1.5;
  g.strokeStyle = css(mix(base, COLORS.steel5, o.rim ?? 0.55), 0.85);
  g.stroke(path);
  g.restore();
  // Core shadow on the lower-right edges.
  g.save();
  g.clip(path);
  g.translate(-1.6, -1.6);
  g.lineWidth = 2.2;
  g.strokeStyle = css(COLORS.ink, 0.4);
  g.stroke(path);
  g.restore();
  g.lineWidth = o.line ?? 1;
  g.strokeStyle = css(COLORS.ink, 0.95);
  g.stroke(path);
}

function poly(g: G, pts: readonly number[], base: number, o?: ShadeOpts): Path2D {
  const p = polyPath(pts);
  plate(g, p, boxOf(pts), base, o);
  return p;
}

function limb(g: G, ax: number, ay: number, bx: number, by: number, ra: number, rb: number, base: number, o?: ShadeOpts): void {
  const c = capsule(ax, ay, bx, by, ra, rb);
  plate(g, c.path, c.box, base, o);
}

function disc(g: G, x: number, y: number, r: number, base: number, o?: ShadeOpts): void {
  plate(g, ellipsePath(x, y, r, r), { x0: x - r, y0: y - r, x1: x + r, y1: y + r }, base, o);
}

function line(g: G, x0: number, y0: number, x1: number, y1: number, color: number, w: number, alpha = 1): void {
  g.beginPath();
  g.moveTo(x0, y0);
  g.lineTo(x1, y1);
  g.lineWidth = w;
  g.lineCap = 'round';
  g.strokeStyle = css(color, alpha);
  g.stroke();
}

/** Flat recess (vent, visor slot). */
function recess(g: G, pts: readonly number[], color: number = MAT.joint): void {
  g.fillStyle = css(color);
  g.fill(polyPath(pts));
}

/** Diagonal hazard stripes clipped to a path. */
function stripes(g: G, path: Path2D, b: Box, color: number, width = 2.6, gap = 3, angle = -0.85): void {
  g.save();
  g.clip(path);
  const cx = (b.x0 + b.x1) / 2;
  const cy = (b.y0 + b.y1) / 2;
  const span = Math.max(b.x1 - b.x0, b.y1 - b.y0) * 1.5 + 8;
  g.translate(cx, cy);
  g.rotate(angle);
  g.fillStyle = css(color);
  for (let s = -span; s < span; s += width + gap) g.fillRect(s, -span, width, span * 2);
  g.restore();
}

// Emissive helpers (drawn on the glow layer, which is composited above the outline).

function glowDot(g: G, x: number, y: number, r: number, color: number, intensity = 1): void {
  if (intensity <= 0.01) return;
  g.save();
  g.globalAlpha = Math.min(1, intensity);
  g.shadowColor = css(color);
  g.shadowBlur = r * 4;
  g.fillStyle = css(color);
  g.beginPath();
  g.arc(x, y, r, 0, TAU);
  g.fill();
  g.shadowBlur = 0;
  g.fillStyle = css(mix(color, 0xffffff, 0.7));
  g.beginPath();
  g.arc(x, y, r * 0.5, 0, TAU);
  g.fill();
  g.restore();
}

function glowLine(g: G, x0: number, y0: number, x1: number, y1: number, w: number, color: number, intensity = 1): void {
  if (intensity <= 0.01) return;
  g.save();
  g.globalAlpha = Math.min(1, intensity);
  g.lineCap = 'round';
  g.shadowColor = css(color);
  g.shadowBlur = w * 3.5;
  g.strokeStyle = css(color);
  g.lineWidth = w;
  g.beginPath();
  g.moveTo(x0, y0);
  g.lineTo(x1, y1);
  g.stroke();
  g.shadowBlur = 0;
  g.strokeStyle = css(mix(color, 0xffffff, 0.7));
  g.lineWidth = Math.max(0.8, w * 0.45);
  g.stroke();
  g.restore();
}

function glowFill(g: G, path: Path2D, color: number, blur: number, alpha: number): void {
  g.save();
  g.globalAlpha = alpha;
  g.shadowColor = css(color);
  g.shadowBlur = blur;
  g.fillStyle = css(color);
  g.fill(path);
  g.restore();
}

// ───────────────────────────── Shared body parts ─────────────────────────────

type FootKind = 'boot' | 'claw' | 'plate';

interface LegStyle {
  thigh: number;
  shin: number;
  knee: number;
  base: number;
  kneeBase: number;
  foot: FootKind;
  /** Hydraulic piston along the shin. */
  piston?: boolean;
}

function foot(g: G, x: number, y: number, rot: number, kind: FootKind, base: number): void {
  g.save();
  g.translate(x, y);
  g.rotate(rot);
  switch (kind) {
    case 'boot':
      poly(g, [-5.5, -1.5, 2.5, -3.2, 10, 0.8, 10.5, 5, -6, 5], base);
      line(g, -4, 2.6, 8.5, 2.6, MAT.joint, 1, 0.7);
      break;
    case 'claw':
      poly(g, [-6.5, 4.5, -3, -1.5, 2, -2.5, 13, 3.4, 13, 4.6, 5, 4.6, 3, 2.6, 0.5, 4.6], base);
      poly(g, [-2, 0, -9, 2.5, -9.5, 4.6, -3, 4.6], shade(base, 0.8));
      break;
    case 'plate':
    default:
      poly(g, [-11, -2.5, 7, -4, 13, 0.5, 14, 6, -12, 6], base);
      line(g, -9, 2.2, 12, 2.2, MAT.joint, 1.2, 0.75);
      break;
  }
  g.restore();
}

function drawLeg(g: G, L: LegPose, s: LegStyle): void {
  limb(g, L.hip.x, L.hip.y, L.knee.x, L.knee.y, s.thigh, s.thigh * 0.82, s.base);
  limb(g, L.knee.x, L.knee.y, L.ankle.x, L.ankle.y, s.shin, s.shin * 0.72, s.base);
  if (s.piston) {
    const dx = L.ankle.x - L.knee.x;
    const dy = L.ankle.y - L.knee.y;
    const d = Math.hypot(dx, dy) || 1;
    const nx = -dy / d;
    const ny = dx / d;
    line(g, L.knee.x + nx * 2.2, L.knee.y + ny * 2.2, L.ankle.x + nx * 1.6 - dx * 0.2, L.ankle.y + ny * 1.6 - dy * 0.2, MAT.metal, 1.1, 0.9);
  }
  disc(g, L.knee.x, L.knee.y, s.knee, s.kneeBase);
  foot(g, L.ankle.x, L.ankle.y, L.foot, s.foot, s.base);
}

// ───────────────────────────── Trooper ─────────────────────────────

function drawTrooper(g: G, gl: G, P: BipedPose): void {
  const { hip, lean } = P;
  drawLeg(g, P.legR, { thigh: 4.4, shin: 3.7, knee: 3.1, base: MAT.armorDark, kneeBase: MAT.frame, foot: 'boot' });

  // Backpack + antenna.
  const bx = hip.x - 10 + lean * 0.5;
  const by = hip.y - 17;
  plate(g, rrectPath(bx - 6.5, by - 9, 11, 17, 3), { x0: bx - 6.5, y0: by - 9, x1: bx + 4.5, y1: by + 8 }, MAT.armorDark);
  recess(g, [bx - 4.5, by - 3, bx + 1.5, by - 3, bx + 1.5, by - 1.8, bx - 4.5, by - 1.8]);
  recess(g, [bx - 4.5, by + 0.5, bx + 1.5, by + 0.5, bx + 1.5, by + 1.7, bx - 4.5, by + 1.7]);
  line(g, bx - 3.5, by - 9, bx - 6.5, by - 21, MAT.metal, 1.5);
  line(g, bx - 3.5, by - 9, bx - 6.5, by - 21, MAT.ink, 0.6, 0.5);
  glowDot(gl, bx - 6.6, by - 21.5, 1.6, MAT.sensor, P.extra);

  // Torso (forward-leaning armoured chest).
  const tp = [hip.x - 8, hip.y + 1, hip.x + 8, hip.y + 1, hip.x + 12 + lean, hip.y - 18, hip.x + 6 + lean, hip.y - 25, hip.x - 6 + lean * 0.8, hip.y - 23];
  poly(g, tp, MAT.armor);
  const cp = [hip.x + 1.5 + lean * 0.6, hip.y - 9, hip.x + 9 + lean * 0.8, hip.y - 10, hip.x + 10.5 + lean, hip.y - 17.5, hip.x + 5 + lean, hip.y - 22];
  poly(g, cp, MAT.paint, { hi: 1.3, rim: 0.35 });
  line(g, hip.x - 5, hip.y - 4, hip.x + 6, hip.y - 5, MAT.ink, 1, 0.7);
  line(g, hip.x - 5.5, hip.y - 8, hip.x + 3, hip.y - 9, MAT.ink, 1, 0.6);
  glowDot(gl, hip.x + 8.5 + lean * 0.9, hip.y - 14, 1.1, MAT.hot, 0.7 * P.sensor);

  // Pelvis.
  plate(g, rrectPath(hip.x - 7.5, hip.y - 3, 15, 8, 2.5), { x0: hip.x - 7.5, y0: hip.y - 3, x1: hip.x + 7.5, y1: hip.y + 5 }, MAT.frame);

  drawLeg(g, P.legF, { thigh: 4.7, shin: 3.9, knee: 3.4, base: MAT.armor, kneeBase: MAT.plate, foot: 'boot', piston: true });

  // Helmet with a forward visor slit.
  g.save();
  g.translate(P.head.x, P.head.y);
  g.rotate(P.headTilt);
  poly(g, [-7.5, -6, 2.5, -8.5, 9, -4.5, 10.5, 0, 8.5, 5.5, -3, 6.5, -8, 2], MAT.armor);
  poly(g, [-7, -5.5, 2.5, -8, 6, -6, -2, -4, -7.5, -2], MAT.paint, { hi: 1.25, rim: 0.3 });
  recess(g, [2, -3.4, 10.4, -2, 10.6, 1, 2, 0.4]);
  recess(g, [4, 3, 8.5, 2.6, 8, 4.4, 4, 4.6], MAT.frame);
  g.restore();
  gl.save();
  gl.translate(P.head.x, P.head.y);
  gl.rotate(P.headTilt);
  glowLine(gl, 3.2, -1.5, 9.8, -0.6, 1.9, MAT.sensor, P.sensor);
  gl.restore();
}

function drawTrooperGun(g: G, gl: G): void {
  // Rifle (pointing right from the shoulder pivot).
  poly(g, [-13, 0.5, -3, -0.5, -2, 8, -12, 9.5], MAT.frame);
  poly(g, [-4, -1.5, 20, -1.5, 24.5, 1.5, 24.5, 8, 9, 10, -3, 8.5], MAT.armor);
  line(g, 0, 1.6, 18, 1.6, MAT.paint, 1.7);
  poly(g, [8, 9, 13, 9, 12, 16.5, 6.8, 15.5], MAT.frame);
  poly(g, [24, 2.2, 38.5, 2.2, 38.5, 6, 24, 6], MAT.metal, { hi: 1.3 });
  poly(g, [38, 1, 44, 1, 44, 7.5, 38, 7.5], MAT.frame);
  recess(g, [39.5, 3.4, 43, 3.4, 43, 5, 39.5, 5], MAT.ink);
  poly(g, [8, -5, 15.5, -5, 15.5, -1.3, 8, -1.3], MAT.frame);
  glowDot(gl, 14.6, -3.1, 1.1, MAT.sensor, 0.9);
  glowDot(gl, 44.6, 4.3, 1.3, MAT.hot, 0.55);

  // Arm: upper arm under the pauldron, forearm over the rifle, fist on the grip.
  limb(g, 0, 0, 1.5, 10, 4.5, 3.8, MAT.armorDark);
  limb(g, 1.5, 10, 11, 7.5, 3.7, 3.2, MAT.armor);
  plate(g, rrectPath(8.2, 4.2, 6.5, 6.6, 2), { x0: 8.2, y0: 4.2, x1: 14.7, y1: 10.8 }, MAT.plate);
  poly(g, [-7.5, -5, 3, -8.5, 9, -3.5, 7.5, 4.5, -5.5, 5.5], MAT.plate);
  poly(g, [-7, -4.2, 3, -7.8, 6.2, -5.6, -4.5, -1.6], MAT.paint, { hi: 1.3, rim: 0.3 });
  line(g, -4.5, 1.8, 6.5, 0.4, MAT.ink, 1, 0.55);
}

function drawTrooperGib(g: G, gl: G, k: number): void {
  if (k === 0) {
    poly(g, [-7.5, -6, 2.5, -8.5, 9, -4.5, 10.5, 0, 8.5, 5.5, -3, 6.5, -8, 2], MAT.armor);
    poly(g, [-7, -5.5, 2.5, -8, 6, -6, -2, -4, -7.5, -2], MAT.paint, { hi: 1.25 });
    recess(g, [2, -3.4, 10.4, -2, 10.6, 1, 2, 0.4]);
    glowLine(gl, 3.2, -1.5, 9.8, -0.6, 1.4, MAT.sensor, 0.35);
  } else if (k === 1) {
    poly(g, [-9, 6, -6, -9, 5, -11, 10, -4, 7, 3, 9, 8, -2, 6, -5, 10], MAT.armor);
    poly(g, [-3, -7, 5, -9, 8, -4, 1, -2], MAT.paint, { hi: 1.25 });
    line(g, -6, 2, 5, 0, MAT.ink, 1, 0.7);
  } else {
    limb(g, -2, -10, 1, 4, 3.9, 3, MAT.armor);
    disc(g, -2, -10, 3.4, MAT.plate);
    foot(g, 1, 5, 0.1, 'boot', MAT.armor);
  }
}

// ───────────────────────────── Hornet ─────────────────────────────

function wing(g: G, px: number, py: number, ang: number, len: number, wid: number, alpha: number, blur: number): void {
  const draw = (a: number, al: number) => {
    const cx = px + Math.cos(a) * len * 0.5;
    const cy = py + Math.sin(a) * len * 0.5;
    const p = ellipsePath(cx, cy, len * 0.5, wid * 0.5, a);
    g.save();
    g.globalAlpha = al;
    const grad = g.createLinearGradient(px, py, px + Math.cos(a) * len, py + Math.sin(a) * len);
    grad.addColorStop(0, css(COLORS.steel5));
    grad.addColorStop(1, css(COLORS.steel4));
    g.fillStyle = grad;
    g.fill(p);
    g.lineWidth = 1;
    g.strokeStyle = css(COLORS.steel5, 0.9);
    g.stroke(p);
    g.restore();
    g.save();
    g.globalAlpha = Math.min(1, al * 1.6);
    line(g, px, py, px + Math.cos(a) * len * 0.92, py + Math.sin(a) * len * 0.92, COLORS.steel3, 0.9);
    g.restore();
  };
  if (blur > 0.05) {
    draw(ang - 0.26 * blur, alpha * 0.35);
    draw(ang + 0.26 * blur, alpha * 0.35);
  }
  draw(ang, alpha);
}

function drawHornet(g: G, gl: G, P: HornetPose): void {
  for (const c of [g, gl]) {
    c.save();
    c.translate(0, P.bob);
    c.rotate(P.tilt);
  }
  wing(g, -4, -8, P.wingR, 22, 6.5, 0.36, P.blur);

  // Abdomen: two tapered segments that curl forward under the body during the wind-up.
  const a1 = 2.72 - P.curl * 0.62;
  const a2 = 2.86 - P.curl * 1.45;
  const mx = -6 + Math.cos(a1) * 12;
  const my = 2 + Math.sin(a1) * 12;
  const tx = mx + Math.cos(a2) * 12;
  const ty = my + Math.sin(a2) * 12;
  const seg2 = capsule(mx, my, tx, ty, 6.6, 3);
  striped(g, seg2.path, seg2.box, MAT.armorDark, MAT.hazard, 2.4, 2.6, a2 + Math.PI / 2);
  const seg1 = capsule(-5, 2, mx, my, 8.4, 6.8);
  striped(g, seg1.path, seg1.box, MAT.armor, MAT.hazard, 2.6, 3.4, a1 + Math.PI / 2);
  // Stinger tip on the abdomen.
  limb(g, tx, ty, tx + Math.cos(a2) * 6, ty + Math.sin(a2) * 6, 1.8, 0.5, MAT.metal);

  // Hooked legs.
  for (let k = 0; k < 3; k++) {
    const lx = -4 + k * 5;
    line(g, lx, 7, lx - 3, 13, MAT.frame, 2);
    line(g, lx - 3, 13, lx + 0.5, 16, MAT.frame, 1.6);
  }

  // Thorax with a lighter carapace and a red stripe.
  plate(g, ellipsePath(1, 0, 13.5, 9.5), { x0: -12.5, y0: -9.5, x1: 14.5, y1: 9.5 }, MAT.armor);
  const cara = new Path2D();
  cara.ellipse(1, -1, 12, 7.5, 0, Math.PI, TAU);
  cara.closePath();
  plate(g, cara, { x0: -11, y0: -8.5, x1: 13, y1: -1 }, MAT.plate);
  line(g, -6, -6.5, 6, -7.5, MAT.paint, 2.2);
  recess(g, [-9, 2, 10, 2, 9, 3.4, -8, 3.4], MAT.frame);

  // Forward drill spike (extends during the wind-up; the dive leads with it).
  const sl = 7 + P.sting * 11;
  const sx = 12;
  const sy = 5.5;
  limb(g, sx, sy, sx + Math.cos(P.stingAng) * sl, sy + Math.sin(P.stingAng) * sl, 2.4, 0.6, MAT.metal, { hi: 1.4 });
  if (P.sting > 0.5) glowDot(gl, sx + Math.cos(P.stingAng) * sl, sy + Math.sin(P.stingAng) * sl, 1.3, MAT.hot, (P.sting - 0.5) * 1.6);

  // Head + compound eye + mandibles + antennae.
  plate(g, ellipsePath(15, 1, 7.5, 7), { x0: 7.5, y0: -6, x1: 22.5, y1: 8 }, MAT.armorDark);
  g.fillStyle = css(MAT.joint);
  g.beginPath();
  g.arc(18.5, 0, 4.8, 0, TAU);
  g.fill();
  line(g, 19, 5, 22.5, 8, MAT.frame, 1.8);
  line(g, 16, 6, 18.5, 9.5, MAT.frame, 1.6);
  line(g, 16, -6, 23, -13, MAT.frame, 1.1);
  line(g, 13, -6.5, 17.5, -15, MAT.frame, 1.1);
  glowDot(gl, 18.8, -0.2, 3.5, MAT.sensor, P.sensor);
  glowDot(gl, 23.2, -13.2, 0.9, MAT.sensor, P.sensor * 0.8);
  glowDot(gl, 17.6, -15.2, 0.9, MAT.sensor, P.sensor * 0.8);

  wing(g, 3, -8, P.wingF, 25, 7.5, 0.48, P.blur);
  for (const c of [g, gl]) c.restore();
}

function drawHornetGib(g: G, gl: G, k: number): void {
  if (k === 0) {
    wing(g, -12, 0, 0, 25, 7.5, 0.6, 0);
  } else if (k === 1) {
    plate(g, ellipsePath(0, 0, 7.5, 7), { x0: -7.5, y0: -7, x1: 7.5, y1: 7 }, MAT.armorDark);
    g.fillStyle = css(MAT.joint);
    g.beginPath();
    g.arc(3.5, -1, 4.5, 0, TAU);
    g.fill();
    glowDot(gl, 3.7, -1.2, 2.4, MAT.sensor, 0.35);
  } else {
    const seg = capsule(-9, -2, 8, 3, 7.5, 3.5);
    striped(g, seg.path, seg.box, MAT.armor, MAT.hazard, 2.6, 3.4, 1.9);
  }
}

// ───────────────────────────── Lancer ─────────────────────────────

function drawLancer(g: G, gl: G, P: BipedPose): void {
  const { hip, lean } = P;
  drawLeg(g, P.legR, { thigh: 3.6, shin: 3, knee: 2.8, base: MAT.armorDark, kneeBase: MAT.frame, foot: 'claw' });

  // Jet pack (two thrusters) — flames when leaping.
  const jx = hip.x - 11 + lean * 0.6;
  const jy = hip.y - 24;
  plate(g, rrectPath(jx - 4, jy, 7, 19, 2.5), { x0: jx - 4, y0: jy, x1: jx + 3, y1: jy + 19 }, MAT.armorDark);
  plate(g, rrectPath(jx + 1, jy + 2, 6, 16, 2.5), { x0: jx + 1, y0: jy + 2, x1: jx + 7, y1: jy + 18 }, MAT.frame);
  poly(g, [jx - 3.5, jy + 18, jx + 2.5, jy + 18, jx + 3.5, jy + 22, jx - 4.5, jy + 22], MAT.metal);
  if (P.extra > 0.05) {
    const flame = polyPath([jx - 3.5, jy + 22, jx + 2.5, jy + 22, jx - 0.5, jy + 22 + 16 * P.extra]);
    glowFill(gl, flame, MAT.hot, 10, 0.9 * P.extra);
    glowLine(gl, jx - 0.5, jy + 22, jx - 0.5, jy + 22 + 9 * P.extra, 1.6, 0xffd9a0, P.extra);
  } else {
    glowDot(gl, jx - 0.5, jy + 21, 1.2, MAT.hot, 0.45);
  }

  // Narrow torso with a red chevron.
  const tp = [hip.x - 5, hip.y + 1, hip.x + 5, hip.y + 1, hip.x + 7.5 + lean, hip.y - 20, hip.x + 3.5 + lean, hip.y - 26, hip.x - 5.5 + lean * 0.8, hip.y - 24];
  poly(g, tp, MAT.armor);
  poly(g, [hip.x - 1 + lean * 0.6, hip.y - 10, hip.x + 5.5 + lean * 0.9, hip.y - 15, hip.x + 5 + lean * 0.95, hip.y - 19, hip.x - 1.5 + lean * 0.7, hip.y - 14], MAT.paint, { hi: 1.3 });
  line(g, hip.x - 3, hip.y - 4, hip.x + 4, hip.y - 5, MAT.ink, 1, 0.7);
  plate(g, rrectPath(hip.x - 5, hip.y - 3, 10, 7, 2), { x0: hip.x - 5, y0: hip.y - 3, x1: hip.x + 5, y1: hip.y + 4 }, MAT.frame);

  drawLeg(g, P.legF, { thigh: 3.9, shin: 3.2, knee: 3, base: MAT.armor, kneeBase: MAT.plate, foot: 'claw', piston: true });

  // Tall helmet with a swept crest and a vertical cyclops lens.
  g.save();
  g.translate(P.head.x, P.head.y);
  g.rotate(P.headTilt);
  poly(g, [-3, -8, -13, -15, -11, -11, -15, -10, -6, -3], MAT.paint, { hi: 1.3, rim: 0.3 });
  poly(g, [-5, -9, 3.5, -10.5, 7, -6.5, 7.5, 5.5, 4, 8.5, -4, 8.5, -6, 2], MAT.armor);
  recess(g, [1.8, -6.5, 6, -6.2, 6, 3.6, 1.8, 3.2]);
  line(g, -4, 4, 3, 4.5, MAT.ink, 1, 0.6);
  g.restore();
  gl.save();
  gl.translate(P.head.x, P.head.y);
  gl.rotate(P.headTilt);
  glowLine(gl, 3.9, -5, 3.9, 2.2, 2.4, MAT.sensor, P.sensor);
  gl.restore();
}

function drawLancerGun(g: G, gl: G): void {
  poly(g, [-21, -2, -8, -3.5, -6.5, 5, -19, 6], MAT.frame);
  poly(g, [-8, -4.5, 30, -4.5, 34.5, -1, 34.5, 4.5, 10, 6.5, -8, 5.5], MAT.armor);
  line(g, -4, -2.4, 26, -2.4, MAT.paint, 1.6);
  plate(g, rrectPath(10, 3.5, 15, 7, 2), { x0: 10, y0: 3.5, x1: 25, y1: 10.5 }, MAT.frame);
  for (let k = 0; k < 3; k++) glowLine(gl, 12.5 + k * 4.5, 6, 12.5 + k * 4.5, 8.4, 1, MAT.sensor, 0.65);
  poly(g, [4, -10, 22, -10, 22, -5.5, 4, -5.5], MAT.frame);
  glowDot(gl, 22.6, -7.7, 1.3, MAT.sensor, 0.9);
  poly(g, [34, -1.6, 74.5, -1.2, 74.5, 3.2, 34, 3.6], MAT.metal, { hi: 1.35 });
  for (const cx of [42, 52, 62]) plate(g, rrectPath(cx, -3.4, 4.2, 8.6, 1.4), { x0: cx, y0: -3.4, x1: cx + 4.2, y1: 5.2 }, MAT.plate);
  poly(g, [72, -5, 85, -2.4, 85, -1, 72, -1], MAT.frame);
  poly(g, [72, 4, 85, 3, 85, 4.6, 72, 7], MAT.frame);
  glowDot(gl, 82.5, 1.5, 1.8, MAT.sensor, 0.75);

  limb(g, 0, 0, 2, 9, 3.6, 3.1, MAT.armorDark);
  limb(g, 2, 9, 11, 5.5, 3.1, 2.8, MAT.armor);
  plate(g, rrectPath(8.2, 2.5, 6, 6, 2), { x0: 8.2, y0: 2.5, x1: 14.2, y1: 8.5 }, MAT.plate);
  const pp = [-5.5, -4.5, 3, -6.5, 6.5, -2, 5.5, 3.5, -4.5, 4.5];
  poly(g, pp, MAT.plate);
  line(g, -4.5, -3.3, 3, -5.4, MAT.paint, 1.8);
}

function drawLancerGib(g: G, gl: G, k: number): void {
  if (k === 0) {
    poly(g, [-3, -8, -13, -15, -11, -11, -15, -10, -6, -3], MAT.paint, { hi: 1.3 });
    poly(g, [-5, -9, 3.5, -10.5, 7, -6.5, 7.5, 5.5, 4, 8.5, -4, 8.5, -6, 2], MAT.armor);
    recess(g, [1.8, -6.5, 6, -6.2, 6, 3.6, 1.8, 3.2]);
    glowLine(gl, 3.9, -5, 3.9, 2.2, 1.6, MAT.sensor, 0.35);
  } else if (k === 1) {
    plate(g, rrectPath(-4, -10, 8, 20, 2.5), { x0: -4, y0: -10, x1: 4, y1: 10 }, MAT.armorDark);
    poly(g, [-3.5, 10, 3.5, 10, 4.5, 14, -4.5, 14], MAT.metal);
  } else {
    limb(g, -6, -12, 4, 6, 3.4, 2.6, MAT.armor);
    foot(g, 4, 7, 0.2, 'claw', MAT.armor);
  }
}

// ───────────────────────────── Bulwark ─────────────────────────────

function drawBulwark(g: G, gl: G, P: BipedPose): void {
  const { hip, lean } = P;
  const legStyle = (base: number, kneeBase: number): LegStyle => ({ thigh: 7, shin: 6.2, knee: 5.4, base, kneeBase, foot: 'plate', piston: true });
  drawLeg(g, P.legR, legStyle(MAT.armorDark, MAT.frame));

  // Rear reactor housing (weak point) with glowing vents.
  const rx = hip.x - 38 + lean * 0.4;
  const ry = hip.y - 53;
  plate(g, rrectPath(rx, ry, 14, 31, 3), { x0: rx, y0: ry, x1: rx + 14, y1: ry + 31 }, MAT.frame);
  for (let k = 0; k < 4; k++) {
    const vy = ry + 6 + k * 6;
    recess(g, [rx + 3, vy, rx + 11, vy, rx + 11, vy + 2.4, rx + 3, vy + 2.4], MAT.ink);
    glowLine(gl, rx + 4, vy + 1.2, rx + 10, vy + 1.2, 1.6, MAT.hot, 0.35 + 0.65 * P.extra);
  }

  // Hunched torso.
  const tp = [
    hip.x - 24, hip.y + 4,
    hip.x + 16, hip.y + 4,
    hip.x + 27 + lean, hip.y - 22,
    hip.x + 21 + lean, hip.y - 50,
    hip.x + 5 + lean, hip.y - 61,
    hip.x - 17 + lean * 0.5, hip.y - 59,
    hip.x - 28, hip.y - 43,
  ];
  poly(g, tp, MAT.armor);
  poly(g, [hip.x - 15, hip.y - 2, hip.x + 13, hip.y - 2, hip.x + 20 + lean * 0.8, hip.y - 20, hip.x - 13, hip.y - 23], MAT.armorDark, { hi: 1.3 });
  line(g, hip.x - 12, hip.y - 10, hip.x + 15 + lean * 0.6, hip.y - 11, MAT.ink, 1, 0.6);
  line(g, hip.x - 20, hip.y - 35, hip.x + 2 + lean * 0.5, hip.y - 40, MAT.ink, 1, 0.55);
  for (const [vx, vy] of [[-8, -30], [-2, -31], [4, -32]] as const) disc(g, hip.x + vx + lean * 0.4, hip.y + vy, 1.4, MAT.metal);
  plate(g, rrectPath(hip.x - 17, hip.y - 4, 30, 11, 3), { x0: hip.x - 17, y0: hip.y - 4, x1: hip.x + 13, y1: hip.y + 7 }, MAT.frame);

  drawLeg(g, P.legF, legStyle(MAT.armor, MAT.plate));

  // Small head sunk into the shoulders with a twin-eye visor.
  g.save();
  g.translate(P.head.x, P.head.y);
  g.rotate(P.headTilt);
  poly(g, [-8, -6, 5, -8.5, 10.5, -3, 10.5, 4.5, -7, 6], MAT.armorDark);
  recess(g, [0.5, -3.6, 10.6, -2.4, 10.6, 1.6, 0.5, 1.2]);
  g.restore();
  gl.save();
  gl.translate(P.head.x, P.head.y);
  gl.rotate(P.headTilt);
  glowLine(gl, 2, -1.2, 9.5, -0.5, 1.2, MAT.sensor, P.sensor * 0.7);
  glowDot(gl, 4.6, -1, 1.7, MAT.sensor, P.sensor);
  glowDot(gl, 8.6, -0.6, 1.7, MAT.sensor, P.sensor);
  gl.restore();

  // Shield arm: thick upper arm + forearm to the grip, under a huge hazard-striped pauldron.
  const s = P.shoulder;
  const ex = hip.x + 22 + lean;
  const ey = hip.y - 31;
  limb(g, s.x, s.y, ex, ey, 7.2, 6.2, MAT.armorDark);
  limb(g, ex, ey, P.grip.x, P.grip.y, 6.4, 5.6, MAT.armor);
  disc(g, ex, ey, 4.6, MAT.plate);
  plate(g, rrectPath(P.grip.x - 5.5, P.grip.y - 6, 11, 12, 3), { x0: P.grip.x - 5.5, y0: P.grip.y - 6, x1: P.grip.x + 5.5, y1: P.grip.y + 6 }, MAT.frame);
  drawPauldron(g, s.x, s.y);
}

/** Huge bulwark pauldron: lit upper plate, hazard-striped lower band. */
function drawPauldron(g: G, x: number, y: number): void {
  const pp = [x - 15, y - 5, x + 1, y - 15, x + 16, y - 10, x + 20, y + 4, x + 8, y + 11, x - 11, y + 8];
  const box = boxOf(pp);
  const pauldron = polyPath(pp);
  const grad = g.createLinearGradient(box.x0, box.y0, box.x1, box.y1);
  grad.addColorStop(0, css(shade(MAT.plate, 1.5)));
  grad.addColorStop(0.5, css(MAT.plate));
  grad.addColorStop(1, css(shade(MAT.plate, 0.6)));
  g.fillStyle = grad;
  g.fill(pauldron);
  const lower = polyPath([x - 13, y + 1, x + 19, y - 2, x + 20, y + 4, x + 8, y + 11, x - 11, y + 8]);
  g.save();
  g.clip(pauldron);
  g.fillStyle = css(MAT.frame);
  g.fill(lower);
  stripes(g, lower, boxOf(pp), MAT.hazard, 3, 3.4, -0.9);
  lightOverlay(g, lower, box);
  g.restore();
  finish(g, pauldron, MAT.plate, {});
  line(g, x - 13, y + 1, x + 19, y - 2, MAT.ink, 1.2, 0.9);
}

function drawBulwarkGib(g: G, gl: G, k: number): void {
  if (k === 0) {
    drawPauldron(g, -2, 0);
  } else if (k === 1) {
    plate(g, rrectPath(-7, -15, 14, 30, 3), { x0: -7, y0: -15, x1: 7, y1: 15 }, MAT.frame);
    for (let i = 0; i < 4; i++) {
      recess(g, [-4, -9 + i * 6, 4, -9 + i * 6, 4, -6.6 + i * 6, -4, -6.6 + i * 6], MAT.ink);
      glowLine(gl, -3, -7.8 + i * 6, 3, -7.8 + i * 6, 1.3, MAT.hot, 0.3);
    }
  } else {
    limb(g, -4, -14, 2, 2, 6.2, 5, MAT.armor);
    foot(g, 2, 4, 0, 'plate', MAT.armor);
  }
}

/** Shield frames: 'up' energy barrier, 'bash' over-charged barrier, 'down' collapsed emitter. */
function drawShield(g: G, gl: G, mode: 'up' | 'bash' | 'down'): void {
  const sh = RIGS.bulwark.shield!;
  const fwd = mode === 'bash' ? 4 : 0;
  // Emitter bracket held in the fist.
  line(g, 7, -15, sh.planeX0 + 1 + fwd, sh.planeY0 + 10, MAT.metal, 2.6);
  line(g, 7, 15, sh.planeX0 + 1 + fwd, sh.planeY1 - 10, MAT.metal, 2.6);
  poly(g, [-3, -19, 6, -21, 10.5, -14, 10.5, 14, 6, 21, -3, 19, 2, 12, 2, -12], MAT.frame, { hi: 1.7 });
  for (const ny of [-12, 0, 12]) glowDot(gl, 8, ny, 1.4, MAT.hot, mode === 'down' ? 0.35 : 0.9);
  if (mode === 'down') return;

  const x0 = sh.planeX0 + fwd;
  const x1 = sh.planeX1 + fwd;
  const y0 = sh.planeY0;
  const y1 = sh.planeY1;
  const slab = polyPath([x0, y0 + 2, x1 - 4, y0, x1, y0 + 12, x1 + (mode === 'bash' ? 3 : 0), (y0 + y1) / 2, x1, y1 - 10, x1 - 4, y1, x0, y1 - 2, x0 + 3, (y0 + y1) / 2]);
  const strength = mode === 'bash' ? 1 : 0.72;
  gl.save();
  const grad = gl.createLinearGradient(x0, 0, x1, 0);
  grad.addColorStop(0, css(MAT.sensor, 0.18 * strength));
  grad.addColorStop(0.7, css(MAT.energy, 0.45 * strength));
  grad.addColorStop(1, css(0xffb070, 0.75 * strength));
  gl.fillStyle = grad;
  gl.fill(slab);
  // Hex lattice inside the barrier.
  gl.clip(slab);
  gl.strokeStyle = css(0xffc29a, 0.42 * strength);
  gl.lineWidth = 1;
  const r = 5;
  const hw = r * Math.sqrt(3);
  for (let row = 0; row * r * 1.5 < y1 - y0 + r * 2; row++) {
    const cy = y0 + row * r * 1.5;
    for (let col = -1; col * hw < x1 - x0 + hw; col++) {
      const cx = x0 + col * hw + (row % 2 ? hw / 2 : 0);
      gl.beginPath();
      for (let k = 0; k < 6; k++) {
        const a = Math.PI / 6 + (k * Math.PI) / 3;
        const px = cx + Math.cos(a) * r;
        const py = cy + Math.sin(a) * r;
        if (k === 0) gl.moveTo(px, py);
        else gl.lineTo(px, py);
      }
      gl.closePath();
      gl.stroke();
    }
  }
  gl.restore();
  // Bright leading edge and dimmer trailing edge.
  glowLine(gl, x1 - 1, y0 + 10, x1 - 0.5 + (mode === 'bash' ? 3 : 0), (y0 + y1) / 2, 2, 0xff9a62, strength + 0.15);
  glowLine(gl, x1 - 0.5 + (mode === 'bash' ? 3 : 0), (y0 + y1) / 2, x1 - 1, y1 - 10, 2, 0xff9a62, strength + 0.15);
  glowLine(gl, x0 + 1, y0 + 6, x0 + 3, y1 - 6, 1, MAT.sensor, 0.5 * strength);
  if (mode === 'bash') {
    for (let k = 0; k < 6; k++) {
      const yy = y0 + 16 + k * ((y1 - y0 - 32) / 5);
      gl.save();
      glowFill(gl, polyPath([x1 + 2, yy - 3, x1 + 9, yy, x1 + 2, yy + 3]), 0xffb070, 6, 0.9);
      gl.restore();
    }
  }
}

// ───────────────────────────── Sheet builder ─────────────────────────────

interface FrameJob {
  name: string;
  w: number;
  h: number;
  /** Painter origin inside the frame. */
  ox: number;
  oy: number;
  draw: (fig: G, glow: G) => void;
  /** Emit an elite outline mask frame `<name>_o` as well. */
  mask: boolean;
}

interface Scratch {
  canvas: HTMLCanvasElement;
  ctx: G;
}

const scratchPool: Scratch[] = [];

function scratch(i: number, w: number, h: number): Scratch {
  let s = scratchPool[i];
  if (!s) {
    const canvas = document.createElement('canvas');
    s = { canvas, ctx: canvas.getContext('2d')! };
    scratchPool[i] = s;
  }
  if (s.canvas.width !== w || s.canvas.height !== h) {
    s.canvas.width = w;
    s.canvas.height = h;
  } else {
    s.ctx.setTransform(1, 0, 0, 1, 0, 0);
    s.ctx.clearRect(0, 0, w, h);
  }
  s.ctx.globalCompositeOperation = 'source-over';
  s.ctx.globalAlpha = 1;
  return s;
}

/** Silhouette of `src` grown by r px, filled with a flat colour. */
function dilate(src: HTMLCanvasElement, r: number, color: string, out: Scratch): void {
  const g = out.ctx;
  const n = 16;
  for (let k = 0; k < n; k++) {
    const a = (k / n) * TAU;
    g.drawImage(src, Math.cos(a) * r, Math.sin(a) * r);
  }
  g.drawImage(src, 0, 0);
  g.globalCompositeOperation = 'source-in';
  g.fillStyle = color;
  g.fillRect(0, 0, out.canvas.width, out.canvas.height);
  g.globalCompositeOperation = 'source-over';
}

const OUTLINE_PX = 2;
const ELITE_MASK_PX = 3;

function buildSheet(scene: Phaser.Scene, key: string, jobs: FrameJob[]): void {
  if (scene.textures.exists(key)) return;
  // Shelf-pack every frame (and its mask) into one canvas.
  const MAX_W = 2048;
  const PAD = 2;
  type Slot = { job: FrameJob; mask: boolean; x: number; y: number };
  const slots: Slot[] = [];
  let x = PAD;
  let y = PAD;
  let rowH = 0;
  let sheetW = 0;
  const place = (job: FrameJob, mask: boolean): void => {
    if (x + job.w + PAD > MAX_W) {
      x = PAD;
      y += rowH + PAD;
      rowH = 0;
    }
    slots.push({ job, mask, x, y });
    x += job.w + PAD;
    rowH = Math.max(rowH, job.h);
    sheetW = Math.max(sheetW, x);
  };
  for (const job of jobs) place(job, false);
  for (const job of jobs) if (job.mask) place(job, true);
  const W = sheetW;
  const H = y + rowH + PAD;
  const tex = scene.textures.createCanvas(key, W, H);
  if (!tex) return;
  const out = tex.context;

  const rendered = new Map<FrameJob, { comp: HTMLCanvasElement; mask: HTMLCanvasElement | null }>();
  for (const job of jobs) {
    const fig = scratch(0, job.w, job.h);
    const glow = scratch(1, job.w, job.h);
    fig.ctx.translate(job.ox, job.oy);
    glow.ctx.translate(job.ox, job.oy);
    job.draw(fig.ctx, glow.ctx);
    fig.ctx.setTransform(1, 0, 0, 1, 0, 0);
    glow.ctx.setTransform(1, 0, 0, 1, 0, 0);

    const ink = scratch(2, job.w, job.h);
    dilate(fig.canvas, OUTLINE_PX, css(COLORS.ink), ink);
    // Persistent canvases per frame (the scratch ones are reused by the next job).
    const comp = document.createElement('canvas');
    comp.width = job.w;
    comp.height = job.h;
    const cg = comp.getContext('2d')!;
    cg.drawImage(ink.canvas, 0, 0);
    cg.drawImage(fig.canvas, 0, 0);
    let mask: HTMLCanvasElement | null = null;
    if (job.mask) {
      const m = scratch(3, job.w, job.h);
      dilate(comp, ELITE_MASK_PX, '#ffffff', m);
      mask = document.createElement('canvas');
      mask.width = job.w;
      mask.height = job.h;
      mask.getContext('2d')!.drawImage(m.canvas, 0, 0);
    }
    cg.drawImage(glow.canvas, 0, 0);
    rendered.set(job, { comp, mask });
  }

  for (const s of slots) {
    const r = rendered.get(s.job)!;
    const src = s.mask ? r.mask : r.comp;
    if (!src) continue;
    out.drawImage(src, s.x, s.y);
    tex.add(s.mask ? outlineFrame(s.job.name) : s.job.name, 0, s.x, s.y, s.job.w, s.job.h);
  }
  tex.refresh();
}

function bodyJobs(rig: RigDef, draw: (g: G, gl: G, anim: (typeof ENEMY_ANIMS)[number], i: number) => void): FrameJob[] {
  const jobs: FrameJob[] = [];
  for (const anim of ENEMY_ANIMS) {
    const spec = rig.anims[anim];
    if (!spec) continue;
    for (let i = 0; i < spec.frames; i++) {
      jobs.push({ name: frameName(anim, i), w: rig.cellW, h: rig.cellH, ox: rig.anchorX, oy: rig.anchorY, mask: true, draw: (g, gl) => draw(g, gl, anim, i) });
    }
  }
  return jobs;
}

function gibJobs(rig: RigDef, draw: (g: G, gl: G, k: number) => void): FrameJob[] {
  const size = Math.round(Math.max(rig.cellW, rig.cellH) * 0.45);
  const jobs: FrameJob[] = [];
  for (let k = 0; k < rig.gibs; k++) jobs.push({ name: `gib${k}`, w: size, h: size, ox: size / 2, oy: size / 2, mask: false, draw: (g, gl) => draw(g, gl, k) });
  return jobs;
}

function gunJob(rig: RigDef, draw: (g: G, gl: G) => void): FrameJob[] {
  const gun = rig.gun;
  if (!gun) return [];
  return [{ name: 'gun', w: gun.w, h: gun.h, ox: gun.pivotX, oy: gun.pivotY, mask: true, draw }];
}

function buildEnemySheet(scene: Phaser.Scene, id: EnemyId): void {
  const rig = RIGS[id];
  let jobs: FrameJob[];
  switch (id) {
    case 'trooper':
      jobs = [...bodyJobs(rig, (g, gl, a, i) => drawTrooper(g, gl, trooperPose(a, i))), ...gunJob(rig, drawTrooperGun), ...gibJobs(rig, drawTrooperGib)];
      break;
    case 'hornet':
      jobs = [...bodyJobs(rig, (g, gl, a, i) => drawHornet(g, gl, hornetPose(a, i))), ...gibJobs(rig, drawHornetGib)];
      break;
    case 'lancer':
      jobs = [...bodyJobs(rig, (g, gl, a, i) => drawLancer(g, gl, lancerPose(a, i))), ...gunJob(rig, drawLancerGun), ...gibJobs(rig, drawLancerGib)];
      break;
    case 'bulwark':
    default:
      jobs = [...bodyJobs(rig, (g, gl, a, i) => drawBulwark(g, gl, bulwarkPose(a, i))), ...gibJobs(rig, drawBulwarkGib)];
      break;
  }
  buildSheet(scene, rig.key, jobs);
  if (rig.shield) {
    const sh = rig.shield;
    buildSheet(
      scene,
      sh.key,
      (['up', 'bash', 'down'] as const).map((mode) => ({ name: mode, w: sh.w, h: sh.h, ox: sh.gripX, oy: sh.gripY, mask: true, draw: (g: G, gl: G) => drawShield(g, gl, mode) })),
    );
  }
}

// ───────────────────────────── Shared FX textures ─────────────────────────────

function canvasTexture(scene: Phaser.Scene, key: string, w: number, h: number, draw: (g: G) => void): void {
  if (scene.textures.exists(key)) return;
  const tex = scene.textures.createCanvas(key, w, h);
  if (!tex) return;
  draw(tex.context);
  tex.refresh();
}

function buildFxTextures(scene: Phaser.Scene): void {
  canvasTexture(scene, ENEMY_FX.aura, 64, 64, (g) => {
    const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.35, 'rgba(255,255,255,0.55)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, 64, 64);
  });

  canvasTexture(scene, ENEMY_FX.hex, 128, 128, (g) => {
    // Hex-cell bubble: bright rim, faint lattice, soft fill.
    const fill = g.createRadialGradient(64, 64, 10, 64, 64, 62);
    fill.addColorStop(0, 'rgba(255,255,255,0.05)');
    fill.addColorStop(0.75, 'rgba(255,255,255,0.16)');
    fill.addColorStop(1, 'rgba(255,255,255,0.45)');
    g.fillStyle = fill;
    g.beginPath();
    g.arc(64, 64, 62, 0, TAU);
    g.fill();
    g.save();
    g.beginPath();
    g.arc(64, 64, 61, 0, TAU);
    g.clip();
    const r = 10;
    const hw = r * Math.sqrt(3);
    for (let row = -1; row < 10; row++) {
      for (let col = -1; col < 9; col++) {
        const cx = col * hw + (row % 2 ? hw / 2 : 0);
        const cy = row * r * 1.5;
        const d = Math.hypot(cx - 64, cy - 64) / 64;
        g.strokeStyle = `rgba(255,255,255,${(0.25 + 0.6 * d * d).toFixed(3)})`;
        g.lineWidth = 1.4;
        g.beginPath();
        for (let k = 0; k < 6; k++) {
          const a = Math.PI / 6 + (k * Math.PI) / 3;
          const px = cx + Math.cos(a) * (r - 1);
          const py = cy + Math.sin(a) * (r - 1);
          if (k === 0) g.moveTo(px, py);
          else g.lineTo(px, py);
        }
        g.closePath();
        g.stroke();
      }
    }
    g.restore();
    g.strokeStyle = 'rgba(255,255,255,0.95)';
    g.lineWidth = 2.5;
    g.beginPath();
    g.arc(64, 64, 61, 0, TAU);
    g.stroke();
  });

  canvasTexture(scene, ENEMY_FX.star, 20, 20, (g) => {
    g.translate(10, 10);
    g.shadowColor = '#ffffff';
    g.shadowBlur = 5;
    g.fillStyle = '#ffffff';
    g.beginPath();
    for (let k = 0; k < 8; k++) {
      const a = (k / 8) * TAU - Math.PI / 2;
      const r = k % 2 === 0 ? 8 : 2.4;
      if (k === 0) g.moveTo(Math.cos(a) * r, Math.sin(a) * r);
      else g.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    }
    g.closePath();
    g.fill();
  });

  canvasTexture(scene, ENEMY_FX.core, 48, 48, (g) => {
    const grad = g.createRadialGradient(24, 24, 0, 24, 24, 24);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.25, 'rgba(255,255,255,0.95)');
    grad.addColorStop(0.5, 'rgba(255,255,255,0.4)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, 48, 48);
  });

  canvasTexture(scene, ENEMY_FX.bar, 4, 4, (g) => {
    g.fillStyle = '#ffffff';
    g.fillRect(0, 0, 4, 4);
  });

  canvasTexture(scene, ENEMY_FX.placeholder, 40, 64, (g) => {
    g.fillStyle = css(COLORS.steel1);
    g.fillRect(2, 2, 36, 60);
    g.strokeStyle = css(COLORS.threat);
    g.lineWidth = 3;
    g.strokeRect(2, 2, 36, 60);
    g.fillStyle = css(COLORS.threat);
    g.fillRect(22, 12, 12, 4);
  });
}

function buildAnims(scene: Phaser.Scene, id: EnemyId): void {
  const rig = RIGS[id];
  if (!scene.textures.exists(rig.key)) return;
  const tex = scene.textures.get(rig.key);
  for (const anim of ENEMY_ANIMS) {
    const spec = rig.anims[anim];
    const key = enemyAnimKey(id, anim);
    if (!spec || scene.anims.exists(key) || !tex.has(frameName(anim, 0))) continue;
    scene.anims.create({
      key,
      frames: Array.from({ length: spec.frames }, (_, i) => ({ key: rig.key, frame: frameName(anim, i) })),
      frameRate: spec.fps,
      repeat: spec.loop ? -1 : 0,
    });
  }
}

/** Builds every enemy texture + animation. Idempotent; keys already present (e.g. from the asset manifest) are kept. */
export function buildEnemyArt(scene: Phaser.Scene): void {
  buildFxTextures(scene);
  for (const id of ENEMY_IDS) {
    try {
      buildEnemySheet(scene, id);
      buildAnims(scene, id);
    } catch (err) {
      console.error(`[enemies] art for "${id}" failed`, err);
    }
  }
}

/** Lazily builds the art if a scene runs without BootScene (sandboxes, previews). */
export function ensureEnemyArt(scene: Phaser.Scene): void {
  if (!scene.textures.exists(RIGS.trooper.key) || !scene.textures.exists(ENEMY_FX.aura)) buildEnemyArt(scene);
}
